/**
 * LINKEDIN POST CHECK — one report per draft for the LinkedIn agent's review deck.
 *
 * Composes checks that already exist and adds the few a personal LinkedIn voice needs. The status is derived
 * here, in code; a model never sets it (`docs/AGENT-RULE-INCIDENTS.md` §15). Ruling:
 * `docs/planning/brand-agent-2026-10-30/45-BOARD-RULING-SYSTEM.md`.
 *
 * Holds (ruling 9): a figure, date, name or quotation absent from the source; facts stated with no source or only a
 * partial one; a name on the person's do-not-name list (clients, prospects, people); a source's hedge dropped; a culprit or cause the source only attributes, stated as fact; the
 * author's own product named; engagement bait; a near-repeat of an earlier post; experience the author never gave;
 * the author's specialty brought into a story that is not about it; a stock contrast or "the real X" line.
 * Warnings: hook past LinkedIn's fold, length, hashtags, a link in the body, slop phrasing. These stay defaults
 * the author's own results can overturn (`35-BOARD-RULING.md` R8).
 *
 * `ready` means nothing checkable failed. It never means the post is true: the fact layer cannot judge claims made
 * in the post's own words (`post-source-support.ts`). The summary line says what was checked, not more.
 *
 * Pure: no model, no I/O. It also runs in the browser, so an edit in the deck is re-checked as the author types.
 */
import { findDroppedHedges } from './ad-copy-claims';
import { checkPostAgainstSource } from './post-source-support';
import { type SlopRuleId, checkProseSlop } from './slop';

export type PostCheckStatus = 'ready' | 'look' | 'held';
export type PostCheckSeverity = 'hold' | 'warn';
export type PostCheckLayer =
  | 'facts'
  | 'source'
  | 'hedge'
  | 'attribution'
  | 'privacy'
  | 'promotion'
  | 'bait'
  | 'repeat'
  | 'shape'
  | 'voice'
  | 'editor'
  | 'writing';

/** Every area the check reads, in order: facts against the source, then the shared writing doctrine, then the
 *  editor's read. A summary counts how many are clear; it never decides anything (the status does). */
export const POST_CHECK_LAYERS: readonly PostCheckLayer[] = [
  'facts',
  'source',
  'hedge',
  'attribution',
  'privacy',
  'promotion',
  'bait',
  'repeat',
  'shape',
  'voice',
  'editor',
  'writing',
];

/** How many check areas found nothing, out of all of them. */
export function clearLayers(report: Pick<PostCheckReport, 'findings'>): {
  clear: number;
  total: number;
} {
  const flagged = new Set(report.findings.map((f) => f.layer));
  return {
    clear: POST_CHECK_LAYERS.filter((l) => !flagged.has(l)).length,
    total: POST_CHECK_LAYERS.length,
  };
}

export interface PostCheckSource {
  readonly name: string;
  readonly text: string;
  /** A paywalled or truncated body. A short source must never pass as a whole one. */
  readonly partial?: boolean;
  readonly publishedAt?: string | null;
}

export interface PostCheckInput {
  readonly text: string;
  readonly sources: readonly PostCheckSource[];
  readonly take?: { readonly text: string; readonly status: 'proposed' | 'confirmed' } | null;
  /** Clients, prospects and people the author must never name in public. Naming one is a hold. */
  readonly doNotName?: readonly string[];
  /** The author's own product or company names. Naming one reads as promotion. */
  readonly ownProductNames?: readonly string[];
  /** Names that may appear without a source: the author, LinkedIn. */
  readonly allowedNames?: readonly string[];
  readonly pastPosts?: readonly string[];
  /** Stems of the author's specialty ("orchestrat"). Allowed only when a source is about them. */
  readonly specialtyTerms?: readonly string[];
  /** Set when the editor read this exact text (its fingerprint matched). */
  readonly editorRead?: boolean;
  /** What the editor said about this exact text and the writer could not fix. Shown, never holding. */
  readonly editorNotes?: readonly { label: string; quote: string; fix: string }[];
  /** Written from the person's own note, so experience in it is theirs. Facts are still checked against the note. */
  readonly fromOwnNote?: boolean;
  /** Real writing samples on file. Under five, the deck says the voice is not learned yet (ruling 4). */
  readonly voiceSampleCount?: number;
  /** The article's own sentence that most cuts against the post's view, verified in the article by the caller.
   *  The post must deal with it. */
  readonly counterpoint?: string;
  readonly developing?: boolean;
  readonly now?: Date;
}

export interface PostCheckFinding {
  readonly layer: PostCheckLayer;
  readonly severity: PostCheckSeverity;
  readonly label: string;
  /** The line of the post that carries the problem. */
  readonly quote: string;
  readonly fix: string;
}

export interface PostCheckReport {
  readonly status: PostCheckStatus;
  readonly findings: PostCheckFinding[];
  /** One honest line for the card: what was checked, or what to look at. */
  readonly summary: string;
  readonly voiceLearned: boolean;
}

export const LINKEDIN_FOLD_CHARS = 210;
export const LINKEDIN_LONG_POST_CHARS = 1300;
export const MAX_HASHTAGS = 3;
/** A paragraph longer than this reads as a wall of text on a phone. */
export const LINKEDIN_BLOCK_CHARS = 450;
const SHORT_PARAGRAPH_CHARS = 260;
export const VOICE_SAMPLES_NEEDED = 5;
/** Share of three-word runs two posts have in common before the newer one reads as a repeat. */
export const REPEAT_OVERLAP = 0.5;

const QUOTE_CHARS = 160;

const clip = (text: string): string => {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length <= QUOTE_CHARS ? t : `${t.slice(0, QUOTE_CHARS).trimEnd()}…`;
};

const DEFAULT_ALLOWED_NAMES = ['LinkedIn'];

/* No trailing `\b`: most of these end in "?", and a word boundary after "?" never matches at the end of a post. */
const BAIT =
  /(?:^|[.!?]\s+)(?:agree|thoughts)\s*\?|\b(?:comment\s+(?:yes|below|["“]?\w+["”]?\s+(?:if|and|below))\b|like\s+if\b|repost\s+if\b|share\s+if\b|tag\s+(?:someone|a\s+friend)\b|drop\s+a\s+["“]?\w+["”]?\s+(?:below|in the comments)\b|follow\s+me\s+for\b)/i;

const URL = /\bhttps?:\/\/\S+|\bwww\.\S+/i;
const HASHTAG = /(^|\s)#[\p{L}\p{N}_]+/gu;

const ATTRIBUTION_IN_SOURCE =
  /\b(?:attribut(?:es|ed|ing)|blam(?:es|ed|ing)|suspect(?:s|ed)?|believed|alleg(?:es|ed|edly)|linked\s+to|assessment|according\s+to|claims?|claimed|says|said|reportedly|accus(?:es|ed))\b/i;
const ATTRIBUTION_IN_POST =
  /\b(?:says|said|according\s+to|reported(?:ly)?|reports|attribut(?:es|ed)|blames?|blamed|alleg(?:es|ed|edly)|suspect(?:s|ed)?|assessment|believes?|claims?|thinks|calls?\s+it|points?\s+to)\b/i;
/** Culprit and cause verbs: the sentences where an attribution matters. */
const CULPRIT_VERB =
  /\b(?:stole|steal|(?:was|were|is|are)\s+behind|responsible|carried\s+out|hacked|caused|cause\s+was|was\s+caused|did\s+it|orchestrated|perpetrat\w*|breached|compromised|attacked|launched\s+the\s+attack)\b/i;

function sentencesOf(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+|\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function words(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s']/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

const CONTENT_WORD_MIN = 4;

function contentWords(text: string): Set<string> {
  return new Set(words(text).filter((w) => w.length >= CONTENT_WORD_MIN));
}

function capitalisedNames(text: string): Set<string> {
  const names = new Set<string>();
  for (const sentence of sentencesOf(text)) {
    const tokens = sentence.split(/\s+/);
    tokens.forEach((token, index) => {
      const clean = token.replace(/[^\p{L}\p{N}]/gu, '');
      if (index > 0 && /^\p{Lu}/u.test(clean) && clean.length > 1) names.add(clean.toLowerCase());
    });
  }
  return names;
}

function shingles(text: string): Set<string> {
  const w = words(text);
  const out = new Set<string>();
  for (let i = 0; i + 2 < w.length; i += 1) out.add(`${w[i]} ${w[i + 1]} ${w[i + 2]}`);
  return out;
}

/**
 * Breaks any paragraph longer than LinkedIn reads comfortably on a phone into paragraphs of one or two sentences.
 * Code, because the model drops blank lines when it revises (three of ten revisions came back as one block on
 * 2026-10-31). Wording is never changed.
 */
export function toShortParagraphs(text: string): string {
  return text
    .trim()
    .split(/\n\s*\n/)
    .flatMap((paragraph) => {
      const p = paragraph.trim();
      if (p.length <= LINKEDIN_BLOCK_CHARS || p.includes('\n')) return [p];
      const out: string[] = [];
      let current = '';
      for (const sentence of p.split(/(?<=[.!?]["”’]?)\s+(?=["“‘]?[A-Z0-9])/)) {
        const next = current ? `${current} ${sentence}` : sentence;
        if (
          current &&
          (next.length > SHORT_PARAGRAPH_CHARS || current.split(/[.!?]\s/).length >= 2)
        ) {
          out.push(current);
          current = sentence;
        } else {
          current = next;
        }
      }
      if (current) out.push(current);
      return out;
    })
    .filter(Boolean)
    .join('\n\n');
}

/** Share of the newer post's three-word runs that also appear in the older one. */
export function repeatOverlap(newer: string, older: string): number {
  const a = shingles(newer);
  if (a.size === 0) return 0;
  const b = shingles(older);
  let shared = 0;
  for (const s of a) if (b.has(s)) shared += 1;
  return shared / a.size;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function firstLine(text: string): string {
  return text.trim().split(/\n/)[0] ?? '';
}

/** Naming the outlet you cite ("Fortune reports") is attribution, not a claim the article must contain. */
function allowedNamesFor(input: PostCheckInput): string[] {
  return [
    ...DEFAULT_ALLOWED_NAMES,
    ...(input.allowedNames ?? []),
    ...input.sources.map((s) => s.name),
  ];
}

function factFindings(input: PostCheckInput): PostCheckFinding[] {
  const allowed = allowedNamesFor(input);
  const whole = input.sources.filter((s) => s.text.trim() && !s.partial);
  const partial = input.sources.filter((s) => s.text.trim() && s.partial);

  const againstNothing = checkPostAgainstSource({
    post: input.text,
    sources: [],
    allowedTerms: allowed,
    now: input.now,
  });
  const statesFacts = againstNothing.unsupported.length > 0;

  if (whole.length === 0) {
    if (!statesFacts) return [];
    const quote = againstNothing.unsupported[0]?.claim ?? firstLine(input.text);
    return partial.length > 0
      ? [
          {
            layer: 'source',
            severity: 'hold',
            label: 'Only part of the source could be read',
            quote: clip(quote),
            fix: 'Open the full article, or cut the figures and names it supplies.',
          },
        ]
      : [
          {
            layer: 'source',
            severity: 'hold',
            label: 'Facts with no source',
            quote: clip(quote),
            fix: 'Add the article this is based on, or keep the post to opinion.',
          },
        ];
  }

  const check = checkPostAgainstSource({
    post: input.text,
    sources: [...whole, ...partial].map((s) => s.text),
    allowedTerms: allowed,
    publishedAt: whole[0]?.publishedAt ?? null,
    now: input.now,
  });
  return check.unsupported.map((u) => ({
    layer: 'facts',
    severity: 'hold',
    label: `${u.kind[0].toUpperCase()}${u.kind.slice(1)} not in the source`,
    quote: clip(u.claim),
    fix: u.reason,
  }));
}

function hedgeFindings(input: PostCheckInput): PostCheckFinding[] {
  const sources = input.sources.filter((s) => s.text.trim()).map((s) => s.text);
  /* Hedges and directives make a claim stronger than its source, so they hold. A dropped condition is left out:
     in commentary it fires on a reason the post never restates ("disputed that figure, saying tickets close
     without the customer replying" against "Former employees dispute that figure"), which strengthens nothing. */
  const strengthening = findDroppedHedges([{ field: 'post', text: input.text }], sources).filter(
    (f) => !f.detail.includes("drops the source's condition"),
  );
  return strengthening.map((f) => ({
    layer: 'hedge',
    severity: 'hold',
    label: "The source's qualifier was dropped",
    quote: clip(f.sentence ?? f.detail),
    fix: f.fix,
  }));
}

function attributionFindings(input: PostCheckInput): PostCheckFinding[] {
  const sourceSentences = input.sources.flatMap((s) => sentencesOf(s.text));
  const attributed = sourceSentences.filter((s) => ATTRIBUTION_IN_SOURCE.test(s));
  if (attributed.length === 0) return [];
  const findings: PostCheckFinding[] = [];
  for (const sentence of sentencesOf(input.text)) {
    if (!CULPRIT_VERB.test(sentence) || ATTRIBUTION_IN_POST.test(sentence)) continue;
    const own = contentWords(sentence);
    const names = capitalisedNames(` ${sentence}`);
    const restates = attributed.some((origin) => {
      const theirs = contentWords(origin);
      const sharedNames = [...capitalisedNames(` ${origin}`)].filter((n) => names.has(n));
      let shared = 0;
      for (const w of own) if (theirs.has(w)) shared += 1;
      return sharedNames.length > 0 && shared >= 2;
    });
    if (restates) {
      findings.push({
        layer: 'attribution',
        severity: 'hold',
        label: 'States as fact what the source only attributes',
        quote: clip(sentence),
        fix: 'Say who says so ("TRM Labs attributes it to…"), or leave the culprit out.',
      });
    }
  }
  return findings;
}

/* Words in a listed name may be joined by spaces, a hyphen, a dot or nothing ("ITC Tax", "ITC-Tax", "itctax"), and
   the name must stand alone: "Rollr" is not found inside "Rollrs" or "unrollr". */
function nameMatcher(name: string): RegExp | null {
  const parts = name
    .trim()
    .split(/[\s\-_.]+/)
    .filter(Boolean)
    .map(escapeRegExp);
  if (parts.length === 0) return null;
  return new RegExp(`(?<![\\p{L}\\p{N}])${parts.join('[\\s\\-_.]*')}(?![\\p{L}\\p{N}])`, 'iu');
}

/** The first listed name the text carries, as the listed name; null when it names none. The scheduling step calls
 *  this on its own so a draft that names one never goes out, approved or not. */
export function namedFromList(text: string, names: readonly string[]): string | null {
  for (const name of names) {
    if (nameMatcher(name)?.test(text)) return name.trim();
  }
  return null;
}

function privacyFindings(input: PostCheckInput): PostCheckFinding[] {
  const name = namedFromList(input.text, input.doNotName ?? []);
  if (!name) return [];
  const re = nameMatcher(name);
  const hit = sentencesOf(input.text).find((s) => re?.test(s)) ?? input.text;
  return [
    {
      layer: 'privacy',
      severity: 'hold',
      label: `Names ${name}, which is on your do-not-name list`,
      quote: clip(hit),
      fix: 'Take the name out. If the point needs it, describe the situation without saying who it was.',
    },
  ];
}

function promotionFindings(input: PostCheckInput): PostCheckFinding[] {
  const names = (input.ownProductNames ?? []).filter((n) => n.trim());
  for (const name of names) {
    const re = new RegExp(`\\b${escapeRegExp(name.trim())}\\b`, 'i');
    const hit = sentencesOf(input.text).find((s) => re.test(s));
    if (hit) {
      return [
        {
          layer: 'promotion',
          severity: 'hold',
          label: 'Names your own product',
          quote: clip(hit),
          fix: 'Keep the point and drop the product name; say "a tool I build" if the context matters.',
        },
      ];
    }
  }
  return [];
}

function baitFindings(input: PostCheckInput): PostCheckFinding[] {
  const hit = sentencesOf(input.text).find((s) => BAIT.test(s));
  return hit
    ? [
        {
          layer: 'bait',
          severity: 'hold',
          label: 'Engagement bait',
          quote: clip(hit),
          fix: 'End on your point, or ask a real question only you would ask.',
        },
      ]
    : [];
}

function repeatFindings(input: PostCheckInput): PostCheckFinding[] {
  for (const past of input.pastPosts ?? []) {
    const overlap = repeatOverlap(input.text, past);
    if (overlap >= REPEAT_OVERLAP) {
      return [
        {
          layer: 'repeat',
          severity: 'hold',
          label: `Repeats an earlier post (${Math.round(overlap * 100)}% the same wording)`,
          quote: clip(firstLine(past)),
          fix: 'Say something new, or skip this one.',
        },
      ];
    }
  }
  return [];
}

/** Experience the author never gave the agent. Code, because a model critic passed "I see engineering teams…". */
const INVENTED_EXPERIENCE =
  /\b(i've seen|i have seen|in my experience|i see (?:[a-z]+\s+)?(?:teams|companies|engineers|clients|people|organi[sz]ations|founders|leaders)|i've watched|my clients|my team|at my company|my (?:own )?(?:product|startup|company)|when i built|i tested|i worked with)\b/i;

export function inventedExperience(post: string): string | null {
  return sentencesOf(post).find((line) => INVENTED_EXPERIENCE.test(line)) ?? null;
}

/** The first sentence using a specialty stem, matched at a word start ("agent" is not "agency"), that no source
 *  uses. */
export function forcedSpecialty(
  post: string,
  sourceText: string,
  terms: readonly string[],
): string | null {
  const stem = (t: string) => new RegExp(`\\b${escapeRegExp(t)}`, 'i');
  const missing = terms.filter((t) => !stem(t).test(sourceText));
  return sentencesOf(post).find((line) => missing.some((t) => stem(t).test(line))) ?? null;
}

/** Lines a reader of LinkedIn has seen a thousand times; each read as AI-written in the 2026-10-31 review. They
 *  hold, because the author asked for a voice that is never corny. "No longer X, but Y" stays local: in ad copy and
 *  captions "no longer open Sundays, but…" is ordinary. The rest moved to the shared doctrine on 2026-11-01
 *  (`slop.ts` binary_contrast and announced_point), and hold here through `HELD_SLOP_RULES`. */
/** "I think", "I expect" and the like, a post's opinion openers. More than two reads as a template: one view, stated
 *  once, carries a post (2026-11-01 blind review: drafts stacked four and five of them). */
const OPINION_MARKER =
  /\b(?:i think|i expect|i believe|i suspect|i doubt|my guess|my bet|in my view)\b/i;
const MAX_OPINION_MARKERS = 2;

/** A chain of "So… That means…" walks a fact into a sweeping forecast (2026-11-01 round 11 review). */
const REASONING_OPENER = /^(?:so|that means|this means|which means)\b/i;
const MAX_REASONING_LEAPS = 2;

/** "TechCrunch reports that…", "Forever Security has found…", "According to…": the post opens on who said it,
 *  the dullest part of the story (round 11 review). */
const OPENS_ON_SOURCE =
  /^(?:according to\b|[A-Z][\w&.'’-]*(?:\s+[A-Z][\w&.'’-]*){0,3}\s+(?:reports?|reported|says|said|has found|found|notes|noted|writes|wrote|announced|published|revealed)\b)/;

const STEM_STOP = new Set([
  'that',
  'this',
  'with',
  'from',
  'have',
  'been',
  'were',
  'will',
  'their',
  'which',
  'about',
]);

/** The post deals with a sentence when it carries at least 40% of that sentence's content words (by stem). */
function mentions(post: string, sentence: string): boolean {
  const stems = (text: string) =>
    text
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length >= 4 && !STEM_STOP.has(w))
      .map((w) => w.slice(0, 5));
  const wanted = [...new Set(stems(sentence))];
  if (wanted.length < 3) return true;
  const have = new Set(stems(post));
  return wanted.filter((w) => have.has(w)).length / wanted.length >= 0.4;
}

const STOCK_LINES: { re: RegExp; label: string; fix: string }[] = [
  {
    re: /\b(?:no longer|not just|never just)\b[^.!?]{1,60}?(?:,\s*|\s+)(?:but|it is|it's)\b/i,
    label: '"No longer X, but Y" contrast',
    fix: 'Say the second half directly.',
  },
];

function voiceFindings(input: PostCheckInput): PostCheckFinding[] {
  const findings: PostCheckFinding[] = [];
  const invented = input.fromOwnNote ? null : inventedExperience(input.text);
  if (invented) {
    findings.push({
      layer: 'voice',
      severity: 'hold',
      label: 'Claims experience you have not given',
      quote: clip(invented),
      fix: 'Say "I think" and the reason, never what you have seen or done.',
    });
  }
  const terms = (input.specialtyTerms ?? []).map((t) => t.toLowerCase()).filter(Boolean);
  if (terms.length > 0) {
    const sourceText = input.sources.map((s) => s.text).join('\n');
    const forced = forcedSpecialty(input.text, sourceText, terms);
    if (forced) {
      findings.push({
        layer: 'voice',
        severity: 'hold',
        label: 'Brings in your specialty where the story is not about it',
        quote: clip(forced),
        fix: 'Make the point about what this story is actually about.',
      });
    }
  }
  const views = sentencesOf(input.text).filter((line) => OPINION_MARKER.test(line));
  if (views.length > MAX_OPINION_MARKERS) {
    findings.push({
      layer: 'writing',
      severity: 'hold',
      label: 'Leans on "I think"',
      quote: clip(views[MAX_OPINION_MARKERS]),
      fix: 'State the view once. The other sentences are facts from the article or reasoning that starts with "So".',
    });
  }
  const leaps = sentencesOf(input.text).filter((line) => REASONING_OPENER.test(line.trim()));
  if (leaps.length > MAX_REASONING_LEAPS) {
    findings.push({
      layer: 'writing',
      severity: 'hold',
      label: 'Leaps from fact to forecast',
      quote: clip(leaps[MAX_REASONING_LEAPS]),
      fix: 'Keep one step of reasoning from the facts to your view; cut the chain of "So" and "That means".',
    });
  }
  const opening = sentencesOf(input.text)[0] ?? '';
  if (OPENS_ON_SOURCE.test(opening.trim())) {
    findings.push({
      layer: 'writing',
      severity: 'hold',
      label: 'Opens on the source',
      quote: clip(opening),
      fix: 'Open on the finding, the number or your view. Name the source in the next sentence.',
    });
  }
  const counter = input.counterpoint?.trim();
  if (counter && !mentions(input.text, counter)) {
    findings.push({
      layer: 'writing',
      severity: 'hold',
      label: "Leaves out the article's own counterpoint",
      quote: clip(counter),
      fix: 'Say it fairly, in a sentence, and show why your view still holds.',
    });
  }
  for (const line of STOCK_LINES) {
    const hit = sentencesOf(input.text).find((s) => line.re.test(s));
    if (hit) {
      findings.push({
        layer: 'writing',
        severity: 'hold',
        label: line.label,
        quote: clip(hit),
        fix: line.fix,
      });
    }
  }
  return findings;
}

/**
 * A closing aphorism: the post ends on a short, unattributed line that states a general truth ("Taste is the
 * bottleneck now."). Readers take it as AI-written; the founder named it on 3 Nov 2026. Returns the line, or null.
 */
export function closingOneLiner(text: string): string | null {
  const paragraphs = text
    .trim()
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  const last = paragraphs[paragraphs.length - 1];
  if (!last || paragraphs.length < 2) return null;
  if (/[?\d]/.test(last) || /[.!?]\s+\S/.test(last)) return null;
  const words = last.split(/\s+/).length;
  if (words > 9) return null;
  if (/\b(?:i|i'm|i'd|i'll|my|me|we|our|you|your|says?|said|according)\b/i.test(last)) return null;
  return /\b(?:is|are|was|wins?|beats?|matters?|becomes?)\b/i.test(last) ? last : null;
}

function shapeFindings(input: PostCheckInput): PostCheckFinding[] {
  const findings: PostCheckFinding[] = [];
  const hook = firstLine(input.text);
  const block = input.text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .find((p) => p.length > LINKEDIN_BLOCK_CHARS);
  if (block) {
    findings.push({
      layer: 'shape',
      severity: 'hold',
      label: 'A wall of text',
      quote: clip(block),
      fix: 'Break it into short paragraphs with a blank line between them.',
    });
  }
  const oneLiner = closingOneLiner(input.text);
  if (oneLiner) {
    findings.push({
      layer: 'writing',
      severity: 'warn',
      label: 'Closing one-liner',
      quote: clip(oneLiner),
      fix: 'End on your point in a full sentence, or on a question to the reader.',
    });
  }
  if (hook.length > LINKEDIN_FOLD_CHARS) {
    findings.push({
      layer: 'shape',
      severity: 'warn',
      label: `First line runs past LinkedIn's "see more" (${hook.length} of ${LINKEDIN_FOLD_CHARS} characters)`,
      quote: clip(hook),
      fix: 'Put the point in a shorter first line.',
    });
  }
  if (input.text.length > LINKEDIN_LONG_POST_CHARS) {
    findings.push({
      layer: 'shape',
      severity: 'warn',
      label: `Long for LinkedIn (${input.text.length} characters)`,
      quote: clip(hook),
      fix: 'Cut to the one point that matters.',
    });
  }
  const hashtags = input.text.match(HASHTAG) ?? [];
  if (hashtags.length > MAX_HASHTAGS) {
    findings.push({
      layer: 'shape',
      severity: 'warn',
      label: `${hashtags.length} hashtags`,
      quote: clip(hashtags.join(' ')),
      fix: `Use ${MAX_HASHTAGS} at most, or none.`,
    });
  }
  const url = input.text.match(URL);
  if (url) {
    findings.push({
      layer: 'shape',
      severity: 'warn',
      label: 'Link in the post',
      quote: clip(url[0]),
      fix: 'Put the link in your first reply.',
    });
  }
  return findings;
}

/** Shared slop rules that hold a LinkedIn post rather than warn: the reader asked for a voice that is never corny. */
const HELD_SLOP_RULES: ReadonlySet<SlopRuleId> = new Set([
  'binary_contrast',
  'announced_point',
  'slogan',
]);

function writingFindings(input: PostCheckInput): PostCheckFinding[] {
  return checkProseSlop(input.text, { emDashBudget: 0 }).findings.map((f) => ({
    layer: 'writing',
    severity: HELD_SLOP_RULES.has(f.ruleId) ? 'hold' : 'warn',
    label: f.label,
    quote: clip(f.quote),
    fix: f.fix,
  }));
}

function summarise(
  input: PostCheckInput,
  status: PostCheckStatus,
  findings: readonly PostCheckFinding[],
  voiceLearned: boolean,
): string {
  const voice = voiceLearned ? '' : ' · Voice not learned yet';
  if (status === 'held') {
    const holds = findings.filter((f) => f.severity === 'hold').length;
    return `${holds === 1 ? 'One thing' : `${holds} things`} to fix before this can go out`;
  }
  if (status === 'look') {
    const warns = findings.length;
    return `${warns === 1 ? 'One thing' : `${warns} things`} to look at${voice}`;
  }
  const names = input.sources
    .filter((s) => s.text.trim() && !s.partial)
    .map((s) => s.name.trim())
    .filter(Boolean);
  const statesFacts =
    checkPostAgainstSource({
      post: input.text,
      sources: [],
      allowedTerms: allowedNamesFor(input),
      now: input.now,
    }).unsupported.length > 0;
  const facts =
    statesFacts && names.length > 0
      ? `Figures, names and quotes found in ${joinNames(names)}`
      : 'No figures or names to check';
  const editor =
    input.editorRead && !(input.editorNotes ?? []).length
      ? ' · Editor read it for hook, view, fit and voice'
      : '';
  return `${facts}${editor}${voice}`;
}

function joinNames(names: readonly string[]): string {
  const unique = [...new Set(names)];
  if (unique.length <= 1) return unique[0] ?? '';
  return `${unique.slice(0, -1).join(', ')} and ${unique[unique.length - 1]}`;
}

export function checkLinkedInPost(input: PostCheckInput): PostCheckReport {
  const findings = [
    ...factFindings(input),
    ...hedgeFindings(input),
    ...attributionFindings(input),
    ...privacyFindings(input),
    ...promotionFindings(input),
    ...baitFindings(input),
    ...repeatFindings(input),
    ...voiceFindings(input),
    ...(input.editorRead ? (input.editorNotes ?? []) : []).map((n) => ({
      layer: 'editor' as const,
      severity: 'warn' as const,
      label: n.label,
      quote: clip(n.quote),
      fix: n.fix,
    })),
    ...shapeFindings(input),
    ...writingFindings(input),
  ];
  const status: PostCheckStatus = findings.some((f) => f.severity === 'hold')
    ? 'held'
    : findings.length > 0
      ? 'look'
      : 'ready';
  const voiceLearned = (input.voiceSampleCount ?? 0) >= VOICE_SAMPLES_NEEDED;
  return {
    status,
    findings,
    summary: summarise(input, status, findings, voiceLearned),
    voiceLearned,
  };
}
